import { Sparkles, Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useState, useRef, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

interface SmartSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => void;
  isSearching?: boolean;
  className?: string;
  disableDropdown?: boolean;
}

const suggestions = [
  { 
    labelEn: 'Mercedes under 3M', 
    labelTh: 'เบนซ์ ต่ำกว่า 3 ล้าน',
    query: 'mercedes under 3m' 
  },
  { 
    labelEn: 'BMW all models', 
    labelTh: 'BMW ทุกรุ่น',
    query: 'bmw' 
  },
  { 
    labelEn: 'Audi under 2M', 
    labelTh: 'Audi ต่ำกว่า 2 ล้าน',
    query: 'audi under 2m' 
  },
  { 
    labelEn: 'Volvo all models', 
    labelTh: 'Volvo ทุกรุ่น',
    query: 'volvo' 
  },
  { 
    labelEn: 'Luxury under 2M', 
    labelTh: 'รถหรู ต่ำกว่า 2 ล้าน',
    query: 'luxury under 2m' 
  },
];

export function SmartSearchBar({
  value,
  onChange,
  onSearch,
  isSearching = false,
  className,
  disableDropdown = false,
}: SmartSearchBarProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t, language } = useLanguage();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSuggestionClick = (query: string) => {
    onChange(query);
    onSearch(query);
    setShowDropdown(false);
    setIsFocused(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim()) {
      onSearch(value);
      setShowDropdown(false);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (!disableDropdown) {
      setShowDropdown(true);
    }
  };

  return (
    <div ref={containerRef} className={cn("relative z-50", className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground z-10" />
        {isSearching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary animate-spin z-10" />
        )}
        <Input
          placeholder={t("Search cars...", "ค้นหารถ...")}
          value={value}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          className={cn(
            "h-10 pl-10 pr-10 bg-card border-border/50 rounded-full focus-visible:ring-2 transition-all duration-300",
            isFocused && "w-full md:w-[320px]"
          )}
        />
      </div>

      {/* Dropdown Panel */}
      {showDropdown && !disableDropdown && (
        <div className="absolute top-full left-0 mt-2 w-full min-w-[280px] bg-card border border-border/50 rounded-lg shadow-lg p-4 z-[9999] animate-fade-in">
          {/* Header */}
          <div className="flex items-center gap-2 text-sm font-medium mb-3 text-foreground">
            <Sparkles className="h-4 w-4 text-primary" />
            <span>{t('Quick Search', 'ค้นหาด่วน')}</span>
          </div>

          {/* Suggestion Chips */}
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => (
              <Badge
                key={suggestion.labelEn}
                onClick={() => handleSuggestionClick(suggestion.query)}
                className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors text-xs bg-secondary text-secondary-foreground border-border/50"
              >
                {language === 'en' ? suggestion.labelEn : suggestion.labelTh}
              </Badge>
            ))}
          </div>

          {/* Footer hint */}
          <div className="mt-3 pt-3 border-t border-border/50 text-xs text-muted-foreground">
            {t(
              'Try: "BMW under 2M" or "Audi"',
              'ลอง: "เบนซ์ ต่ำกว่า 3 ล้าน" หรือ "BMW"'
            )}
          </div>
        </div>
      )}
    </div>
  );
}
